/**
 * Capability Management Platform (CMP) — catalog contracts.
 *
 * The catalog is a code-defined, strongly-typed registry (mirroring the AI
 * capability registry in `lib/ai/contracts.ts` / `lib/ai/capabilities`). It
 * defines the taxonomy and dependency graph statically; only per-tenant *state*
 * (overrides, edition grants, audit) lives in the database. See
 * `lib/capabilities/catalog/index.ts` for how the layers assemble into `CATALOG`.
 *
 * IMPORTANT: this is distinct from `hooks/useCapabilityContext.ts` /
 * `types/capability.ts`, which is the *work-role* context (does this user have
 * work to do here?). The CMP answers a different question: is this feature
 * *available* for this workspace/role/user given edition + admin configuration?
 */

/** The four logical layers of the platform hierarchy. */
export type CapabilityLayer = 'module' | 'journey' | 'capability' | 'platform_service';

/**
 * Progressive-experience tiers. Numeric so thresholds compare with `<=`.
 * 1 Simple · 2 Growing · 3 Professional · 4 Enterprise.
 */
export enum MaturityLevel {
  Simple = 1,
  Growing = 2,
  Professional = 3,
  Enterprise = 4,
}

/**
 * Commercial edition ordinal. These map 1:1 to `billing_plan_catalog.plan_key`
 * — they are NOT a parallel edition system. Billing remains authoritative.
 */
export type EditionKey = 'free' | 'starter' | 'professional' | 'enterprise';

/** Ordinal used to compare editions (higher = more capable). */
export const EDITION_RANK: Record<EditionKey, number> = {
  free: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

/**
 * Billing feature keys the CMP treats as hard floors. Mirrors
 * `BillingFeatureKey` in `lib/billing/entitlementResolver.ts`. A capability
 * bound to one of these can never be enabled by a lower-authority scope when
 * billing reports it `false`.
 */
export type CapabilityBillingFeatureKey =
  | 'ai_assistant'
  | 'premium_api'
  | 'iot_connectors'
  | 'automation_metering';

/**
 * Every node key is `<layer-plural>.<slug>`, e.g. `'journeys.request_to_inspection'`,
 * `'modules.jobs'`, `'capabilities.qr_codes'`, `'services.ai_platform'`. Narrowed
 * to `keyof typeof CATALOG` at the registry (see catalog/index.ts).
 */
export type CapabilityNodeKey = string;

interface BaseCatalogNode {
  key: CapabilityNodeKey;
  layer: CapabilityLayer;
  title: string;
  description: string;
  /**
   * Hard prerequisites: this node resolves disabled unless ALL of these resolve
   * enabled. May cross layers (a journey depends on capabilities; a capability
   * depends on platform services) — this is what makes the DAG unified.
   */
  dependsOn: readonly CapabilityNodeKey[];
  /** Abstract features this node satisfies (decouples requires/provides from concrete keys). */
  provides: readonly string[];
  /** Abstract features this node needs at least one provider for. */
  requires: readonly string[];
  /** Lowest commercial edition that MAY use this node. Merged with billing; never contradicts a billing `false`. */
  minEdition: EditionKey;
  /** Progressive-experience tier this node belongs to. */
  maturityLevel: MaturityLevel;
  /** When true, gated behind a `beta_programme` scope; never on by edition alone. */
  betaFlag: boolean;
  /** Internal-dev only — never visible to end tenants regardless of overrides (hard floor). */
  internalOnly: boolean;
  /** Free-form classification (`'write'` marks capabilities that mutate data — gated by access_state). */
  tags: readonly string[];
  /** When false the node is retired: resolves disabled, but the key stays for audit stability. */
  active: boolean;
  /**
   * Set when this row is part of every plan and cannot be switched off: the
   * Matrix shows it locked as "Always included" with this one-line reason,
   * and no edition grant or override disables it (app resolver, edge
   * functions and the database all skip it). Use it only for something every
   * workspace needs, or that has nothing of its own to switch off.
   * `testing/__tests__/matrixKeysAreEnforced.current.test.ts` proves every
   * other row really switches something off.
   */
  alwaysIncluded?: string;
}

export interface ModuleDefinition extends BaseCatalogNode {
  layer: 'module';
  /** Ties into `shared/command-center/route-manifest.ts` item ids so nav/route gating can look up canonical paths. */
  routeManifestIds: readonly string[];
}

export interface JourneyDefinition extends BaseCatalogNode {
  layer: 'journey';
  /** The module this journey lives under. */
  moduleKey: CapabilityNodeKey;
  /** Ordered capability keys composing the end-to-end flow. */
  steps: readonly CapabilityNodeKey[];
}

export interface CapabilityDefinition extends BaseCatalogNode {
  layer: 'capability';
  /** Optional binding to an AI capability key (`lib/ai/registry.ts`) — keeps AI as a provider, not a duplicate. */
  aiCapabilityKey?: string;
  /** Optional billing feature this capability is fronted by. When billing reports it false, the capability is force-disabled. */
  billingFeatureKey?: CapabilityBillingFeatureKey;
}

export interface PlatformServiceDefinition extends BaseCatalogNode {
  layer: 'platform_service';
  /** Infra kind a capability leans on. */
  serviceKind: 'edge_function' | 'realtime' | 'storage' | 'sync_outbox' | 'cron' | 'push' | 'core';
}

export type AnyCatalogNode =
  | ModuleDefinition
  | JourneyDefinition
  | CapabilityDefinition
  | PlatformServiceDefinition;

/**
 * Identity helpers for inference — mirror `defineAiCapability`. They perform no
 * work at runtime; they exist so each catalog entry is checked against its
 * layer's shape at author time with full autocomplete.
 */
export const defineModule = (d: ModuleDefinition): ModuleDefinition => d;
export const defineJourney = (d: JourneyDefinition): JourneyDefinition => d;
export const defineCapability = (d: CapabilityDefinition): CapabilityDefinition => d;
export const definePlatformService = (d: PlatformServiceDefinition): PlatformServiceDefinition => d;

/** The scopes at which a capability can be enabled/disabled. Ordered by specificity in the resolver. */
export type CapabilityScopeType =
  | 'global'
  | 'environment'
  | 'edition'
  | 'beta_programme'
  | 'industry'
  | 'journey_pack'
  | 'workspace'
  | 'role'
  | 'user'
  | 'internal_dev';

export type CapabilityEffect = 'enable' | 'disable';
